import { css, html, LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";
import "prismjs/prism.js";
import { setPageMetaDescription, setPageMetaTitle } from "../functions";
import { getRouteContent } from "../routes";
import { a11yStyles, baseStyles, prismStyles } from "../styles";
import type { Content } from "../entities";

@customElement("umb-market-content")
export class UmbMarketContent extends LitElement {
  @state()
  private _content?: Content;

  connectedCallback() {
    super.connectedCallback();
    this._content = getRouteContent();

    setPageMetaTitle(this._content.title);
    setPageMetaDescription(this._content.description);

    document.querySelector("umb-market-header")?.scrollIntoView();
    window.addEventListener("content-view-data-updated", this.#updateContent);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener(
      "content-view-data-updated",
      this.#updateContent
    );
  }

  async firstUpdated() {
    const content = this.renderRoot?.querySelector("article");
    if (!content) return;

    const languages = new Set(
      Array.from(content.querySelectorAll('code[class*="language-"]')).map(
        (n) => n.classList.value.replace("language-", "")
      )
    );

    await Promise.all(
      Array.from(languages).map((l) =>
        import(/* @vite-ignore */ `prismjs/components/prism-${l}.min.js`).catch((_) => _)
      )
    );

    window.Prism.highlightAllUnder(content);
  }

  #updateContent = async (e: Event) => {
    this._content = (e as CustomEvent).detail.content;
    this.requestUpdate("_content");

    if (this._content?.afterUpdate && this.shadowRoot) {
      await this.updateComplete;
      this._content.afterUpdate(this.shadowRoot);
    }
  };

  render() {
    return html` <umb-market-page-title
        .pageTitle=${this._content?.displayName ?? this._content?.title}
      ></umb-market-page-title>
      <article>${this._content?.html}</article>`;
  }

  static styles = [
    a11yStyles,
    baseStyles,
    prismStyles,
    css`
      article {
        max-width: 80ch;
        margin: 0 auto;
      }
      code {
        padding: 1px 4px;
        background-color: white;
        border-radius: 4px;
      }

      pre {
        background-color: white !important;
        border-radius: 4px;
      }

      table {
        border-collapse: collapse;
        margin: var(--l-spacer) 0;
      }

      thead tr {
        background-color: var(--uui-color-default);
        color: var(--grey);
        text-align: left;
      }

      tbody tr {
        border-bottom: 1px solid #dddddd;
      }

      tbody tr:nth-of-type(even) {
        background-color: #f3f3f3;
      }

      tbody tr:last-of-type {
        border-bottom: 2px solid var(--uui-color-default);
      }

      th,
      td {
        padding: 12px 15px;
        font-size: 14px;
      }

      td p {
        font-size: 14px;
      }
    `,
  ];
}
